import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  RadioGroup,
  HStack,
  Radio,
  Flex,
  Button,
  Textarea,
  Text,
  useToast,
} from '@chakra-ui/react'
import { AiFillCamera } from 'react-icons/ai'
import { useForm, Controller } from 'react-hook-form'
import { ScreenshotButton } from './ScreenshotButton'
import { useState } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { api } from '../../services/api'
import { user } from '../../helpers/user'
import { useQueryClient } from 'react-query'

const schema = yup.object().shape({
  sistema: yup.string().required('Campo obrigatório'),
  classificacao: yup.string().required('Campo obrigatório'),
  descricao: yup.string().required('Campo obrigatório'),
})

export function WidgetForm() {
  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  })

  const queryClient = new useQueryClient()

  const [screenshot, setScreenshot] = useState(null)

  const codUsuario = user?.codigo_usuario

  const toast = useToast()

  function base64ToBlob(base64) {
    const byteCharacters = atob(base64.split(',')[1])
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset++) {
      const byte = byteCharacters.charCodeAt(offset)
      byteArrays.push(byte)
    }

    return new Blob([new Uint8Array(byteArrays)], { type: 'image/png' })
  }

  async function onSubmit(data) {
    if (!screenshot) {
      setError('screenshot', {
        type: 'manual',
        message: 'Você precisa tirar uma foto da tela',
      })

      return
    }

    const blob = base64ToBlob(screenshot)

    const file = new File([blob], 'screenshot.png', {
      type: 'image/png',
    })

    const params = {
      ...data,
      screenshot: file,
    }

    const formData = new FormData()

    formData.append('tipo_chamado', params.sistema)
    formData.append('prioridade', params.classificacao)
    formData.append('detalhes_problemas', params.descricao)
    formData.append('imagem', params.screenshot)
    formData.append('codigo_usuario', codUsuario)

    const response = await api.post(
      '/site/v2/crm-controle/inserir-chamado-sistema',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    if (response.status !== 200) {
      toast({
        title: 'Erro!',
        description: 'Ocorreu um erro ao enviar o chamado',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top-right',
      })
      return
    }

    toast({
      title: 'Sucesso!',
      description: 'Chamado enviado com sucesso',
      status: 'success',
      duration: 9000,
      isClosable: true,
      position: 'top-right',
    })

    queryClient.invalidateQueries('listarChamadosSistema')
    setScreenshot(null)
    reset()
  }

  function handlePaste(e) {
    const items = e.clipboardData.items

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile()

        const reader = new FileReader()

        reader.onload = function (event) {
          const base64Image = event.target.result

          setScreenshot(base64Image)
        }

        reader.readAsDataURL(blob)
      }
    }
  }

  return (
    <Flex flexDir="column" gap={3} as="form" onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        as="fieldset"
        backgroundColor="white"
        padding=".5rem"
        borderRadius="0.5rem"
        boxShadow="0 0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.2)"
      >
        <FormLabel
          as="legend"
          textAlign="center"
          backgroundColor="gray.50"
          margin={0}
        >
          O Problema está relacionado a qual sistema?
        </FormLabel>
        <Controller
          render={({ field }) => (
            <RadioGroup {...field}>
              <HStack spacing="24px">
                <Radio value="Site">Site</Radio>
                <Radio value="Crm">Crm</Radio>
                <Radio value="Sankhya">Sankhya</Radio>
                <Radio value="Desktop">Desktop</Radio>
              </HStack>
              <HStack spacing="24px">
                <Radio value="Telefonia">Telefonia</Radio>
                <Radio value="Guru">Guru</Radio>
                <Radio value="Email">Email</Radio>
                <Radio value="Outros">Outros</Radio>
              </HStack>
            </RadioGroup>
          )}
          name="sistema"
          control={control}
          defaultValue=""
        />
        {errors.sistema && (
          <Text as="span" m={0} color="red.500" fontSize={'.8rem'}>
            {errors.sistema.message}
          </Text>
        )}
      </FormControl>
      <FormControl
        as="fieldset"
        backgroundColor="white"
        padding=".5rem"
        borderRadius="0.5rem"
        boxShadow="0 0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.2)"
      >
        <FormLabel
          as="legend"
          textAlign="center"
          backgroundColor="gray.50"
          margin={0}
        >
          Qual é a classificação do problema?
        </FormLabel>
        <Controller
          render={({ field }) => (
            <RadioGroup {...field}>
              <HStack spacing="24px">
                <Radio value="Baixa">Baixa</Radio>
                <Radio value="Media">Media</Radio>
                <Radio value="Alta">Alta</Radio>
                <Radio value="Muito alta">Muito alta</Radio>
              </HStack>
            </RadioGroup>
          )}
          name="classificacao"
          control={control}
          defaultValue=""
        />
        {errors.classificacao && (
          <Text as="span" m={0} color="red.500" fontSize={'.8rem'}>
            {errors.classificacao.message}
          </Text>
        )}
      </FormControl>

      <ScreenshotButton
        screenshot={screenshot}
        onScreenshotTook={setScreenshot}
      />
      {errors.screenshot && (
        <Text as="span" m={0} color="red.500" fontSize={'.8rem'}>
          {errors.screenshot.message}
        </Text>
      )}

      <Textarea
        placeholder="Descreva o problema"
        {...register('descricao')}
        resize="none"
        height="10rem"
        onPaste={handlePaste}
      />
      {errors.descricao && (
        <Text as="span" m={0} color="red.500" fontSize={'.8rem'}>
          {errors.descricao.message}
        </Text>
      )}

      <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
        Enviar
      </Button>
    </Flex>
  )
}
